/*
 * Storefront search suggestions.
 *
 * Shopify's Predictive Search API is not available for this storefront's ja
 * buyer locale. Suggestions therefore use the lightweight search alternate
 * view (`view=suggest`) while search-normalize.js remains the owner of the
 * query contract.
 */
(() => {
  'use strict';

  const debounceMs = 180;

  const requestUrl = (endpoint, query) => {
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('view', 'suggest');
    return url.toString();
  };

  const close = (input, results) => {
    results.replaceChildren();
    results.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  };

  const render = (input, results, products) => {
    results.replaceChildren();
    const list = document.createElement('ul');
    list.className = 'predictive-search__list';
    list.id = `${results.id}-list`;
    list.setAttribute('role', 'listbox');

    products.forEach((product, index) => {
      if (!product || typeof product.url !== 'string' || typeof product.title !== 'string') return;
      const item = document.createElement('li');
      item.className = 'predictive-search__option';
      item.id = `${results.id}-option-${index + 1}`;
      item.setAttribute('role', 'option');
      const link = document.createElement('a');
      link.className = 'predictive-search__item';
      link.href = product.url;
      link.textContent = product.title;
      item.append(link);
      list.append(item);
    });

    if (!list.childElementCount) {
      close(input, results);
      return;
    }
    results.append(list);
    results.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };

  const bind = (form) => {
    if (!(form instanceof HTMLFormElement) || form.dataset.predictiveSearchReady === 'true') return;
    const input = form.querySelector('[data-search-normalize-input]');
    const results = form.querySelector('[data-predictive-search-results]');
    const endpoint = form.dataset.predictiveSearchUrl;
    if (!(input instanceof HTMLInputElement) || !(results instanceof HTMLElement) || !endpoint) return;

    form.dataset.predictiveSearchReady = 'true';
    let timer;
    let controller;
    let requestNumber = 0;

    const request = () => {
      const normalise = window.TorecaSearchNormalise;
      const query = typeof normalise === 'function' ? normalise(input.value) : input.value.trim();
      if (!query) {
        close(input, results);
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();
      const currentRequest = ++requestNumber;
      fetch(requestUrl(endpoint, query), { signal: controller.signal, headers: { Accept: 'application/json' } })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error(String(response.status))))
        .then((payload) => {
          if (currentRequest !== requestNumber) return;
          const products = payload?.products;
          render(input, results, Array.isArray(products) ? products : []);
        })
        .catch((error) => {
          if (error.name !== 'AbortError' && currentRequest === requestNumber) close(input, results);
        });
    };

    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(request, debounceMs);
    });
    input.addEventListener('keydown', (event) => {
      const links = Array.from(results.querySelectorAll('.predictive-search__item'));
      if (event.key === 'Escape') {
        close(input, results);
      } else if (event.key === 'ArrowDown' && links.length) {
        event.preventDefault();
        links[0].focus();
      }
    });
    results.addEventListener('keydown', (event) => {
      const links = Array.from(results.querySelectorAll('.predictive-search__item'));
      const current = links.indexOf(document.activeElement);
      if (event.key === 'Escape') {
        event.preventDefault();
        close(input, results);
        input.focus();
      } else if (event.key === 'ArrowDown' && current >= 0 && current < links.length - 1) {
        event.preventDefault();
        links[current + 1].focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (current > 0) links[current - 1].focus();
        else input.focus();
      }
    });
    form.addEventListener('submit', () => close(input, results));
    form.addEventListener('focusout', () => window.setTimeout(() => {
      if (!form.contains(document.activeElement)) close(input, results);
    }, 0));
    document.addEventListener('pointerdown', (event) => {
      if (!form.contains(event.target)) close(input, results);
    });
  };

  const bindAll = (scope) => scope.querySelectorAll?.('[data-predictive-search]').forEach(bind);

  // Kept exportable so the endpoint contract is testable without a browser.
  if (typeof module !== 'undefined' && module.exports) module.exports = { requestUrl };
  if (typeof document === 'undefined') return;

  document.addEventListener('DOMContentLoaded', () => bindAll(document));
  document.addEventListener('shopify:section:load', (event) => bindAll(event.target));
})();
